import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import './SplitLayout.css';

/**
 * SplitLayout — optional activity rail / sidebar / main / optional rail.
 * On narrow viewports, CSS collapses sidebar/rail; resize handles hide below md.
 * activity = narrow icon column (use Tabs orientation="vertical"); not a separate shell.
 */
export function SplitLayout({
  activity = null,
  sidebar,
  main,
  rail,
  resizable = false,
  storageKey,
  defaultSizes = { sidebar: 22, main: 56, rail: 22 },
  className = '',
}) {
  const autoSaveId = storageKey ? `onyx-split-${storageKey}` : undefined;

  const content = useMemo(() => {
    if (!resizable) {
      return (
        <>
          {activity ? (
            <nav className="onyx-split__activity" data-testid="onyx-split-activity">
              {activity}
            </nav>
          ) : null}
          {sidebar ? (
            <aside className="onyx-split__sidebar" data-testid="onyx-split-sidebar">
              {sidebar}
            </aside>
          ) : null}
          <main className="onyx-split__main" data-testid="onyx-split-main">
            {main}
          </main>
          {rail ? (
            <aside className="onyx-split__rail" data-testid="onyx-split-rail">
              {rail}
            </aside>
          ) : null}
        </>
      );
    }

    const panels = [];
    if (sidebar) {
      panels.push(
        <Panel
          key="sidebar"
          defaultSize={defaultSizes.sidebar}
          minSize={12}
          collapsible
          className="onyx-split__sidebar"
          data-testid="onyx-split-sidebar"
        >
          {sidebar}
        </Panel>,
      );
      panels.push(
        <PanelResizeHandle
          key="sidebar-handle"
          className="onyx-split__handle"
          data-testid="onyx-split-handle-sidebar"
        />,
      );
    }
    panels.push(
      <Panel
        key="main"
        defaultSize={defaultSizes.main}
        minSize={30}
        className="onyx-split__main"
        data-testid="onyx-split-main"
      >
        {main}
      </Panel>,
    );
    if (rail) {
      panels.push(
        <PanelResizeHandle
          key="rail-handle"
          className="onyx-split__handle"
          data-testid="onyx-split-handle-rail"
        />,
      );
      panels.push(
        <Panel
          key="rail"
          defaultSize={defaultSizes.rail}
          minSize={12}
          collapsible
          className="onyx-split__rail"
          data-testid="onyx-split-rail"
        >
          {rail}
        </Panel>,
      );
    }
    return (
      <PanelGroup
        direction="horizontal"
        autoSaveId={autoSaveId}
        className="onyx-split__group"
      >
        {panels}
      </PanelGroup>
    );
  }, [activity, sidebar, main, rail, resizable, defaultSizes, autoSaveId]);

  return (
    <div
      className={[
        'onyx-split',
        activity ? 'onyx-split--with-activity' : '',
        resizable ? 'onyx-split--resizable' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="onyx-split-layout"
    >
      {resizable && activity ? (
        <nav className="onyx-split__activity" data-testid="onyx-split-activity">
          {activity}
        </nav>
      ) : null}
      {content}
    </div>
  );
}

SplitLayout.propTypes = {
  activity: PropTypes.node,
  sidebar: PropTypes.node,
  main: PropTypes.node,
  rail: PropTypes.node,
  resizable: PropTypes.bool,
  storageKey: PropTypes.string,
  defaultSizes: PropTypes.shape({
    sidebar: PropTypes.number,
    main: PropTypes.number,
    rail: PropTypes.number,
  }),
  className: PropTypes.string,
};

export const ResizablePanels = SplitLayout;

export default SplitLayout;
